import jsbarcode from 'jsbarcode';
import { chunk, groupBy, sumBy, sortBy, isEmpty, pick, get, keyBy, omit, mapValues, } from 'lodash';
import { format as formatDate } from 'date-fns';
import numeral from 'numeral';

import env from './env';
import { idWithTenant, } from './shared/util';
import { labelString, } from './shared/models/product';
import { formatPlanOfShippingRequest, } from './shared/models/shippingRequest';
import { prefectures, } from './shared/config';
import { computeGBFcSortNumber, computeDocumentNumberForBarcode, } from './shared/models/shippingRequest';

function drawLine (canvas, scale, x, y, x2, y2, c, w) {
  const context = canvas.getContext('2d');
  context.beginPath();
  context.moveTo(x * scale, y * scale);
  context.lineTo(x2 * scale, y2 * scale)
  context.strokeStyle = c;
  context.lineWidth = w * scale;
  context.stroke();
}

function drawText (canvas, scale, text, x, y, size = 15) {
  const context = canvas.getContext('2d');
  context.font = `${size}px Consolas`;
  context.fillText(text, x * scale, y * scale);
}

function drawImage (canvas, scale, image, x, y, x2, y2) {
  const context = canvas.getContext('2d');
  context.drawImage(image, x * scale, y * scale, x2 != null ? x2 * scale : undefined, y2 != null ? y2 * scale : undefined);
}

function drawBarcode (canvas, scale, text, x, y, x2, y2, displayValue = false, width = 1, format = 'CODE128', fontSize) {
  const canvasBarcode = document.createElement('canvas');
  jsbarcode(canvasBarcode, text, { displayValue, width: width * scale, format, fontSize, });
  drawImage(canvas, scale, canvasBarcode, x, y, x2, y2);
}

export function drawGbAmazonShippingLabel(amazonFcData, canvas, scale, labelWidth, labelHeight, tenant, sender, shippingRequest, count, index, barcodeX, barcodeW) {
  const margin = 1;
  const boxWidth = labelWidth - 2;
  const boxHeight = labelHeight - 5;
  const context = canvas.getContext('2d');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, labelWidth * scale, labelHeight * scale);
  context.fillStyle = '#000';

  const _drawLine = (canvas, x, y, x2, y2, c, w) => drawLine(canvas, scale, (margin + x), (margin + y), (margin + x2), (margin + y2), c, w);
  const _drawText = (canvas, text, x, y, size = 30) => drawText(canvas, scale, text, (margin + x), (margin + y), size);
  const _drawImage = (canvas, image, x, y, x2, y2) => drawImage(canvas, scale, image, (margin + x), (margin + y), (margin + x2), (margin + y2));
  const _drawBarcode = (canvas, text, x, y, x2, y2, displayValue, width, format) => drawBarcode(canvas, scale, text, (margin + x), (margin + y), (margin + x2), (margin + y2), displayValue, width, format);

  // NOTE: 周りの罫線
  _drawLine(canvas, 0, 0, boxWidth, 0, '#000', 0.5);
  _drawLine(canvas, boxWidth, 0, boxWidth, 78, '#000', 0.5);
  _drawLine(canvas, boxWidth, 100, boxWidth, boxHeight, '#000', 0.5);
  _drawLine(canvas, boxWidth, boxHeight, 0, boxHeight, '#000', 0.5);
  _drawLine(canvas, 0, boxHeight, 0, 0, '#000', 0.5);

  // NOTE: 横線
  _drawLine(canvas, 0, 39, boxWidth, 39, '#000', 0.5);
  _drawLine(canvas, 0, 43, boxWidth, 43, '#000', 0.5);
  _drawLine(canvas, 0, 60, boxWidth, 60, '#000', 0.5);
  _drawLine(canvas, 0, 78, boxWidth, 78, '#000', 0.5);
  _drawLine(canvas, 0, 100, boxWidth, 100, '#000', 0.5);

  // NOTE: 縦線
  _drawLine(canvas, 8, 0, 8, 39, '#000', 0.5);
  _drawLine(canvas, 25, 39, 25, 43, '#000', 0.5);
  _drawLine(canvas, 60, 39, 60, 43, '#000', 0.5);
  _drawLine(canvas, 8, 60, 8, 78, '#000', 0.5);

  _drawText(canvas, 'お', 2.5, 14);
  _drawText(canvas, '届', 2.5, 21);
  _drawText(canvas, '先', 2.5, 28);
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.DestinationFulfillmentCenterId, 10, 18, 90);
  _drawText(canvas, amazonFcData.find(_ => _['コード'] === formatPlanOfShippingRequest(shippingRequest).plan.DestinationFulfillmentCenterId)?.['名称']?.replace('Amazon', '').replace('FC', '') || '', 10, 37, 48);
  _drawBarcode(canvas, computeGBFcSortNumber(amazonFcData, shippingRequest), barcodeX, 6, barcodeW, 20, false, 1, 'ITF')
  _drawText(canvas, computeGBFcSortNumber(amazonFcData, shippingRequest), 51, 29);

  _drawText(canvas, 'お問合せ番号', 2, 42.2);
  _drawText(canvas, chunk(computeDocumentNumberForBarcode(shippingRequest), 4).map(_ => _.join('')).join('-'), 28, 42.2);
  _drawText(canvas, `${index + 1}/${count}`, 65, 42.2);
  _drawBarcode(canvas, 'a' + computeDocumentNumberForBarcode(shippingRequest) + 'a', 5, 44.8, 67, 12.7, false, 1, 'codabar')

  _drawText(canvas, '荷', 2.5, 65);
  _drawText(canvas, '送', 2.5, 70);
  _drawText(canvas, '人', 2.5, 75);
  _drawText(canvas, tenant.name, 10, 65);
  _drawText(canvas, [prefectures[sender.prefecture], sender.city, sender.address1].join(''), 10, 69);
  _drawText(canvas, sender.address2 || '', 10, 73);
  _drawText(canvas, `Tel: ${sender.phone}`, 10, 77);

  _drawText(canvas, '納', 2.5, 83);
  _drawText(canvas, '品', 2.5, 88);
  _drawText(canvas, '番', 2.5, 93);
  _drawText(canvas, '号', 2.5, 98);
  _drawBarcode(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipmentId + 'U' + numeral(index + 1).format('000'), 9, 81.5, 67, 14)
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipmentId + 'U' + numeral(index + 1).format('000'), 32, 98, 28);

  _drawText(canvas, env('GB_CENTER_NAME') || 'XDC大阪南港', 2, 104);
  _drawText(canvas, 'Tel: ' + env('GB_PHONE') || '06-4703-3172', 2, 108);
  _drawText(canvas, `出荷日: ${formatDate(new Date(), 'yyyy/MM/dd')}`, 47, 108);
}

export function drawFukuyamaAmazonShippingLabel(amazonFcData, canvas, scale, labelWidth, labelHeight, tenant, sender, shippingRequest, count, index, barcodeX, barcodeW, showsLines = false) {
  const margin = 1;
  const boxWidth = labelWidth - 2;
  const boxHeight = labelHeight - 2;
  const context = canvas.getContext('2d');
  const now = new Date();
  context.fillStyle = '#fff';
  context.fillRect(0, 0, labelWidth * scale, labelHeight * scale);
  context.fillStyle = '#000';

  const _drawLine = (canvas, x, y, x2, y2, c, w) => drawLine(canvas, scale, (margin + x), (margin + y), (margin + x2), (margin + y2), c, w);
  const _drawText = (canvas, text, x, y, size = 30) => drawText(canvas, scale, text, (margin + x), (margin + y), size * (scale / 10));
  const _drawImage = (canvas, image, x, y, x2, y2) => drawImage(canvas, scale, image, (margin + x), (margin + y), (margin + x2), (margin + y2));
  const _drawBarcode = (canvas, text, x, y, x2, y2, displayValue, width, format) => drawBarcode(canvas, scale, text, (margin + x), (margin + y), (margin + x2), (margin + y2), displayValue, width, format);

  if(showsLines) {
    // NOTE: 周りの罫線
    _drawLine(canvas, 0, 0, boxWidth, 0, '#000', 0.5);
    _drawLine(canvas, boxWidth, 0, boxWidth, boxHeight, '#000', 0.5);
    _drawLine(canvas, boxWidth, boxHeight, 0, boxHeight, '#000', 0.5);
    _drawLine(canvas, 0, boxHeight, 0, 0, '#000', 0.5);

    // NOTE: 横線
    _drawLine(canvas, 0, 20, boxWidth, 20, '#000', 0.5);
    _drawLine(canvas, 0, 59, boxWidth, 59, '#000', 0.5);
    _drawLine(canvas, 0, 80, boxWidth, 80, '#000', 0.5);
    _drawLine(canvas, 0, 109, boxWidth, 109, '#000', 0.5);
    _drawLine(canvas, 0, 128, boxWidth, 128, '#000', 0.5);
    _drawLine(canvas, 0, 143, boxWidth, 143, '#000', 0.5);
  }

  // 発店・着店
  const amazonFcDataItem = amazonFcData.find(_ => _['コード'] === formatPlanOfShippingRequest(shippingRequest).plan.DestinationFulfillmentCenterId)
  _drawText(canvas, amazonFcDataItem?.['福通着店コード'], 2, 15, 150);
  _drawText(canvas, '発泉大津', 30, 5, 27);
  _drawText(canvas, '着', 30, 8, 27);
  _drawText(canvas, amazonFcDataItem?.['福通支店'], 34, 11, 49);

  // 荷受人
  _drawText(canvas, '〒' + formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress.PostalCode, 78, 25, 40);
  _drawText(canvas, ['StateOrProvinceCode', 'City', 'AddressLine1'].map(_ => formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress[_]).join(''), 2, 31, 40);
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress.AddressLine2 || '', 2, 37, 40);
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress.Name, 2, 49, 47);

  // 送り状バーコード
  const barcodeDcumentNumber = 'A' + computeDocumentNumberForBarcode(shippingRequest) + numeral(index + 1).format('00') + 'A';
  const displayDocumentNumber = chunk(computeDocumentNumberForBarcode(shippingRequest).split('').reverse(), 4).reverse().map(_ => _.reverse().join('')).join('-');
  _drawBarcode(canvas, barcodeDcumentNumber, 1, 60, 67, 12.7, false, 1, 'codabar')
  _drawText(canvas, barcodeDcumentNumber, 19, 76, 36);
  _drawText(canvas, now.getFullYear(), 74, 66, 30);
  _drawText(canvas, now.getMonth() + 1, 88, 66, 30);
  _drawText(canvas, now.getDate(), 95, 66, 30);
  _drawText(canvas, '発店: 452 元払', 75, 72, 37);
  _drawText(canvas, '送り状番号' + displayDocumentNumber, 68, 77, 28);

  // 荷送人
  _drawText(canvas, '〒' + sender.postalCode, 5, 83.5, 24);
  _drawText(canvas, [prefectures[sender.prefecture], sender.city, sender.address1].join(''), 5, 87, 26);
  _drawText(canvas, sender.address2 || '', 5, 90.5, 26);
  _drawText(canvas, sender.name, 5, 94.2, 26);
  _drawText(canvas, sender.phone, 40, 97.9, 26);
  _drawText(canvas, count, 80, 91, 50);

  // 荷札
  // 荷受人
  _drawText(canvas, '〒' + formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress.PostalCode, 5, 115, 21);
  _drawText(canvas, ['StateOrProvinceCode', 'City', 'AddressLine1'].map(_ => formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress[_]).join(''), 5, 117.5, 21);
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress.AddressLine2 || '', 5, 120, 21);
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipToAddress.Name, 5, 123, 27);
  // 荷送人
  _drawText(canvas, '〒' + sender.postalCode, 5, 131 + 0, 21);
  _drawText(canvas, [prefectures[sender.prefecture], sender.city, sender.address1].join(''), 5, 131 + 2.5, 21);
  _drawText(canvas, sender.address2 || '', 5, 131 + 5, 21);
  _drawText(canvas, sender.name, 5, 131 + 7.5, 21);
  _drawText(canvas, sender.phone, 32, 131 + 10, 21);
  // 日付
  _drawText(canvas, now.getFullYear(), 76, 115, 30);
  _drawText(canvas, now.getMonth() + 1, 88, 115, 30);
  _drawText(canvas, now.getDate(), 95, 115, 30);
  // 個数
  _drawText(canvas, `${index + 1} / ${count}`, 89, 120, 35);
  // 送り状番号
  _drawText(canvas, displayDocumentNumber, 73, 136, 37);
  // FBA納品番号
  _drawText(canvas, '納', 5.5, 148, 30);
  _drawText(canvas, '品', 5.5, 148 + 3, 30);
  _drawText(canvas, '番', 5.5, 148 + 6, 30);
  _drawText(canvas, '号', 5.5, 148 + 9, 30);
  _drawBarcode(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipmentId + 'U' + numeral(index + 1).format('000'), 12, 143, 85, 14)
  _drawText(canvas, formatPlanOfShippingRequest(shippingRequest).plan.ShipmentId + 'U' + numeral(index + 1).format('000'), 44, 143 + 16, 28);
}

export function drawProductLabel(canvas, labelWidth, labelHeight, product, ng) {
  const text = idWithTenant(product.ref).replace(env('TENANT_PREFIX'), '') + (ng ? '-N' : '');
  const canvasBarcode = document.createElement('canvas');
  jsbarcode(canvasBarcode, text, { fontSize: 20 });
  canvas.width = canvasBarcode.width;
  canvas.height = canvasBarcode.height;
  drawImage(canvas, 1, canvasBarcode, 0, 0, canvasBarcode.width, canvasBarcode.height)
}

export function drawProductLabels24(canvas, items, scale) {
  const labelWidth = 66;
  const labelHeight = 33.9;
  const context = canvas.getContext('2d');
  const fontSize = 3.7;
  context.fillStyle = 'white';
  context.fillRect(0, 0, 210 * scale, 297 * scale);
  context.fillStyle = 'black';
  items.map((item, i) => {
    const { product } = item;
    const offsetX = Math.floor(i / 8) * labelWidth + 6;
    const offsetY = (i % 8) * labelHeight + 12.9;
    const textOffsetX = 3;
    const barcodeHeight = 16;
    const id = idWithTenant(product.ref).replace(env('TENANT_PREFIX'), '');
    drawBarcode(canvas, scale, id, offsetX + 2.5, offsetY, labelWidth - 5, barcodeHeight, false);
    drawText(canvas, scale, id, offsetX + textOffsetX, offsetY + barcodeHeight + 3, fontSize * scale);
    // product.name = '出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示';
    const productDesctiption = product.name.length > 13 ? product.name.slice(0, 13) + '...' : product.name;
    drawText(canvas, scale, product.sku, offsetX + textOffsetX, offsetY + barcodeHeight + 7, fontSize * scale);
    drawText(canvas, scale, productDesctiption, offsetX + textOffsetX, offsetY + barcodeHeight + 11, fontSize * scale);
  });
}

export function drawProductLabels40(canvas, items, scale, type) {
  const labelWidth = 52.5;
  const labelHeight = 29.7;
  const context = canvas.getContext('2d');
  context.fillStyle = 'white';
  context.fillRect(0, 0, 210 * scale, 297 * scale);
  context.fillStyle = 'black';
  items.map((item, i) => {
    const { product } = item;
    const offsetX = Math.floor(i / 10) * labelWidth;
    const offsetY = (i % 10) * labelHeight;
    const textOffsetX = 3;
    const barcodeHeight = 15;
    const labelType = ({
      id: 'id',
      fnsku: 'fnsku',
      mix: product.isAmazonProduct ? 'fnsku' : 'id',
    })[type];
    ({
      id: () => {
        let fontSize = 3;
        const id = idWithTenant(product.ref).replace(env('TENANT_PREFIX'), '');
        drawBarcode(canvas, scale, id, offsetX + 3.5, offsetY, labelWidth - 7, barcodeHeight, false);
        drawText(canvas, scale, id, offsetX + textOffsetX, offsetY + barcodeHeight + 3, fontSize * scale);
        // product.name = '出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示';
        drawText(canvas, scale, product.sku, offsetX + textOffsetX, offsetY + barcodeHeight + 6.5, fontSize * scale);
        fontSize = 2.4;
        const productDesctiption = product.name.length > 18 ? [product.name.slice(0, 9), product.name.slice(-9)].join('...') : product.name;
        drawText(canvas, scale, productDesctiption, offsetX + textOffsetX, offsetY + barcodeHeight + 10, fontSize * scale);
      },
      fnsku: () => {
        const fontSize = 2.4;
        const fnsku = labelString(product);
        const canvasBarcode = document.createElement('canvas');
        jsbarcode(canvasBarcode, fnsku, { fontSize: 20 });
        drawImage(canvas, scale, canvasBarcode, offsetX + 3.5, offsetY, labelWidth - 7, barcodeHeight + 4);

        // product.name = '出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示';
        const productDesctiption = product.name.length > 18 ? [product.name.slice(0, 9), product.name.slice(-9)].join('...') : product.name;
        drawText(canvas, scale, productDesctiption, offsetX + textOffsetX, offsetY + barcodeHeight + 6.5, fontSize * scale);
        drawText(canvas, scale, '新品', offsetX + textOffsetX, offsetY + barcodeHeight + 10, fontSize * scale);
      },
    })[labelType]();
  });
}

export function drawAmazonProductLabel(canvas, labelWidth, labelHeight, product) {
  const fnsku = labelString(product);
  const canvasBarcode = document.createElement('canvas');
  jsbarcode(canvasBarcode, fnsku, { fontSize: 20 });
  canvas.width = canvasBarcode.width;
  canvas.height = canvasBarcode.height + 40;
  const context = canvas.getContext('2d');
  context.fillStyle = 'white';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = 'black';
  drawImage(canvas, 1, canvasBarcode, 0, 0, canvasBarcode.width, canvasBarcode.height)

  // product.name = '出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示出荷指示';
  const productDesctiption = product.name.length > 18 ? [product.name.slice(0, 9), product.name.slice(-9)].join('...') : product.name;
  drawText(canvas, 1, productDesctiption, 10, canvasBarcode.height + 10, 15);
  drawText(canvas, 1, '新品', 10, canvasBarcode.height + 30, 15);
}

export function drawUnknownInventoryLabel(canvas, labelWidth, labelHeight, receivingRequest) {
  const text = idWithTenant(receivingRequest.ref).replace(env('TENANT_PREFIX'), '') + '-U';
  const canvasBarcode = document.createElement('canvas');
  jsbarcode(canvasBarcode, text, { fontSize: 15 });
  canvas.width = canvasBarcode.width;
  canvas.height = canvasBarcode.height;
  drawImage(canvas, 1, canvasBarcode, 0, 0, canvasBarcode.width, canvasBarcode.height)
}

export function drawFnskuLabelsTitleLabel(canvas, scale, labelWidth, labelHeight, locationKey, product, quantity) {
  const margin = 1;
  const boxWidth = labelWidth - 0;
  const boxHeight = labelHeight - 0;
  const context = canvas.getContext('2d');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, labelWidth * scale, labelHeight * scale);
  context.fillStyle = '#000';

  const _drawLine = (canvas, x, y, x2, y2, c, w) => drawLine(canvas, scale, (margin + x), (margin + y), (margin + x2), (margin + y2), c, w);
  const _drawText = (canvas, text, x, y, size = 30) => drawText(canvas, scale, text, (margin + x), (margin + y), size);
  const _drawImage = (canvas, image, x, y, x2, y2) => drawImage(canvas, scale, image, (margin + x), (margin + y), (margin + x2), (margin + y2));

  _drawText(canvas, `ロケ: ${locationKey}`, 2, 5);
  _drawText(canvas, `商品ID: ${idWithTenant(product.ref)}`, 2, 9);
  _drawText(canvas, `FNSKU: ${product.fnsku}`, 2, 13);
  _drawText(canvas, `SKU: ${product.sku}`, 2, 17);
  _drawText(canvas, `数量: ${quantity}`, 2, 21);
}

export function drawReceivementLabels(canvas, items, scale) {
  const labelWidth = 105;
  const labelHeight = 99;
  const barcodeWidth = 60;
  const barcodeHeight = 20;
  const barcodePadding = (labelWidth - barcodeWidth) / 2
  const textOffsetX = 3;
  const rowTextsCount = 30;
  const context = canvas.getContext('2d');
  context.fillStyle = 'white';
  context.fillRect(0, 0, 210 * scale, 297 * scale);
  context.fillStyle = 'black';
  items.map((item, i) => {
    let fontSize = 3.5;
    const { product, receivingRequest, quantity, } = item;
    const offsetX = Math.floor(i / 3) * labelWidth;
    const offsetY = (i % 3) * labelHeight;
    const labelType = product.isAmazonProduct ? 'fnsku' : 'id';
    const productDesctiption = product.name.length > 18 ? [product.name.slice(0, 9), product.name.slice(-9)].join('...') : product.name;
    drawText(canvas, scale, '入荷ID: ' + idWithTenant(receivingRequest.ref), offsetX + textOffsetX, offsetY + 10, fontSize * scale);
    drawText(canvas, scale, 'SKU: ' + product.sku, offsetX + textOffsetX, offsetY + 17, fontSize * scale);
    drawText(canvas, scale, '商品名:', offsetX + textOffsetX, offsetY + 24, fontSize * scale);
    drawText(canvas, scale, product.name.slice(0, rowTextsCount), offsetX + textOffsetX, offsetY + 28, fontSize * scale);
    drawText(canvas, scale, product.name.slice(rowTextsCount, rowTextsCount * 2), offsetX + textOffsetX, offsetY + 32, fontSize * scale);
    drawText(canvas, scale, product.name.slice(rowTextsCount * 2, rowTextsCount * 3), offsetX + textOffsetX, offsetY + 36, fontSize * scale);
    drawText(canvas, scale, product.name.slice(rowTextsCount * 3, rowTextsCount * 4), offsetX + textOffsetX, offsetY + 40, fontSize * scale);
    ({
      id: () => {
        let fontSize = 3;
        const id = idWithTenant(product.ref);
        drawBarcode(canvas, scale, id, offsetX + barcodePadding, offsetY + 50, barcodeWidth, barcodeHeight, false);
        drawText(canvas, scale, id, offsetX + barcodePadding, offsetY + 50 + barcodeHeight + 3, fontSize * scale);
      },
      fnsku: () => {
        const fnsku = labelString(product);
        const canvasBarcode = document.createElement('canvas');
        jsbarcode(canvasBarcode, fnsku, { fontSize: 20 });
        drawImage(canvas, scale, canvasBarcode, offsetX + barcodePadding, offsetY + 50, barcodeWidth, barcodeHeight + 4);
      },
    })[labelType]();
    fontSize = 5;
    drawText(canvas, scale, quantity + '個入', offsetX + textOffsetX, offsetY + 85, fontSize * scale);
  });
}
